import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { WalletPaymentOverlay, WalletPaymentStep } from 'components/WalletPaymentOverlay';
import { trackPurchase, purchaseItem, purchaseCustomer } from 'utils/tracking';
import { HTTP } from 'utils/http';

interface WalletCompleteResponse {
  SaleId?: number;
  ShoppingCart?: { Id?: string } | null;
  ServiceId?: number | string | null;
  Amount?: number | null;
  PaymentMethod?: string | null;
}

/**
 * Retour d'un paiement par portefeuille (Apple Pay, Google Pay).
 *
 * Mindbody ramène le client sur cette route avec le `paymentAccessToken` de
 * l'aller, et rien d'autre : ni statut, ni motif, que le paiement ait abouti ou
 * non (support API, 22/09/2026). Le seul moyen de savoir ce qui s'est passé est
 * donc de tenter la finalisation : un paiement refusé ou abandonné a rendu le
 * jeton inutilisable, et l'appel échoue. Cet appel est rejouable sans risque de
 * double vente, ce qui autorise une seconde tentative après une coupure.
 */
export function WalletCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState<WalletPaymentStep>('finalizing');
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const started = useRef(false);

  useEffect(() => {
    // Le routeur est en hash : selon que Mindbody accroche sa query avant ou
    // après le fragment, le jeton est dans l'un ou l'autre. On regarde les deux.
    const fromSearch = new URLSearchParams(window.location.search).get('paymentAccessToken');
    const token = fromSearch || searchParams.get('paymentAccessToken');

    if (started.current) return;
    started.current = true;

    if (!token) {
      Sentry.captureMessage('Wallet: no payment token on return', { level: 'warning', tags: { flow: 'wallet' } });
      setErrorCode('WalletTokenMissing');
      setStep('failed');
      return;
    }

    (async () => {
      try {
        const response = (await HTTP.post('/sale/walletcomplete', { AccessToken: token }).send()) as WalletCompleteResponse;

        if (!response?.SaleId) throw new Error('WalletPaymentNotCompleted');

        setStep('crediting');

        trackPurchase({
          transactionId: response.ShoppingCart?.Id ?? String(response.SaleId),
          value: Number(response.Amount) || 0,
          items: [purchaseItem(response.ServiceId ?? undefined, Number(response.Amount) || 0)],
          paymentType: response.PaymentMethod || 'Apple Pay',
          customer: purchaseCustomer({}),
        });

        Sentry.captureMessage('Wallet: purchase completed', {
          level: 'info',
          tags: { flow: 'wallet', payment_method: response.PaymentMethod ?? 'unknown' },
          extra: { saleId: response.SaleId },
        });

        setStep('success');
        setTimeout(() => navigate('/account/services', { replace: true }), 1800);
      } catch (error) {
        const code =
          (error as { ErrorCode?: string })?.ErrorCode ?? (error as Error)?.message ?? 'WalletPaymentNotCompleted';

        // Mindbody n'a pas tranché : surtout ne pas annoncer un échec, le
        // paiement a peut-être abouti et la reprise côté serveur finira le
        // travail. Le support Mindbody a signalé le 28/09/2026 que présenter ce
        // cas comme une erreur avait déjà produit de vraies réclamations chez
        // une autre intégration.
        if (code === 'WalletPaymentPending') {
          Sentry.captureMessage('Wallet: payment still pending on return', {
            level: 'warning',
            tags: { flow: 'wallet' },
          });
          setStep('pending');
          return;
        }
        Sentry.captureMessage('Wallet: purchase failed on return', {
          level: 'error',
          tags: { flow: 'wallet' },
          extra: { errorCode: code },
        });
        setErrorCode(code);
        setStep('failed');
      }
    })();
  }, [navigate, searchParams]);

  return (
    <WalletPaymentOverlay
      isOpen
      currentStep={step}
      errorMessage={errorCode}
      onRetry={() => navigate('/tarifs', { replace: true })}
      onGoToAccount={() => navigate('/account/services', { replace: true })}
    />
  );
}
